import { Box, Grid2, IconButton, Paper, Typography } from "@mui/material"
import { createRef, useCallback, useEffect, useRef, useState } from "react";
import { resetContinuousChartData } from "./Common/CommonFunctions";
import EditVisualizationDialog from "./ContinuousChart/EditVisualizationDialog";
import ContinuousSignalChart from "./ContinuousChart/ContinuousSignalChart";
import { Close } from "@mui/icons-material";
import AddChartButton from "./ContinuousChart/AddChartButton";
import CreateChartPlaceholder from "./ContinuousChart/CreateChartPlaceholder";
import ChartActions from "../DigitalTwinV3/Tabs/BatteryVitals/ChartActions";
import { getCSVDataForExportFromChart } from "../../Helper/DigitalTwin/DigitalTwinFunctions";
import DownloadCSV from "../../Helper/CSVOperations/DownloadCSV";
import { toTitleCase } from "../../Helper/Formatters/TextFormatter";
import { useTranslation } from "react-i18next";
import CloseChartAlertDialog from "./Common/CloseChartAlertDialog";
import ZoomResetView from "../DigitalTwinV3/components/ZoomResetView";
import ApexCharts from 'apexcharts';
import { getBaseXAxisSettings } from "../../Helper/DigitalTwin/VitalChartConfigurations";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { calculateIntervalFromTimeWindow, formatChartTimestampForApi } from "../../Helper/Charts/ChartHelper";
import { alertListAction } from "../../Api/BmsFaultsApi";
import { platformAlertListAction, buildPlatformAlertsCsv, getAlertRelatedSignalsList } from "../../Api/PlatformAlertsApi";
import { csvColumns } from "../BmsFaults/AlertConfigs";
import { fetchDigitalTwinHierarchy, getHierarchyEntityTypes } from "../../Helper/DigitalTwin/DigitalTwinFunctions";
import { extractEntities, extractUniqueLevels, fetchDigitalTwinHierarchyLevelSignals, transformTree, getShuffledColors, ANNOTATION_SOURCE } from "./Common/CommonFunctions";
import { buildEventsHierarchy, fetchEventAnnotations } from "./Common/eventsData";
import { buildRelatedSignalPaths } from "./Common/relatedSignalMapping";
import { usePagesContent } from "../../Hooks/usePagesContent";
import { usePlatformAlertsAccess } from "../../Hooks/usePlatformAlertsAccess";
import EntityIdDisplayBox from "./Common/EntityIdDisplayBox";
import { useEntityIdDisplay } from "./Common/entityIdDisplay";

dayjs.extend(utc);
dayjs.extend(timezone);

const ContinuousChartVisualization = ({ batteryID, selectedPrimaryGranularity, selectedDate, routeParams, batteryInfo = {}, selectedEntity = null }) => {

    const { t } = useTranslation(['common', 'digital_twin'])
    const chartGroupName = 'custom-comparison-chart';
    const hasProcessedRouteParams = useRef(false);
    const pageContent = usePagesContent();
    const hasSmartAlertsAccess = usePlatformAlertsAccess();
    // Identifier of the entity picked in the search box; the ID box and the CSV both use it.
    const entityIdDisplay = useEntityIdDisplay(selectedEntity, batteryInfo);
    const [hierarchyInputData, setHierarchyInputData] = useState({});
    const [eventHierarchyInputData, setEventHierarchyInputData] = useState({});
    const levelSignalsResponseRef = useRef(null);
    const relatedSignalsRef = useRef(null);

    // Store the initial chart key for route params to ensure consistency
    const initialChartKeyRef = useRef(routeParams?.events ? `chart-autoload-${Date.now()}` : null);

    // Initialize with loading state if route params exist
    const getInitialChartsData = () => {
        if (routeParams?.events && initialChartKeyRef.current) {
            return {
                [initialChartKeyRef.current]: {
                    chartData: {
                        isValid: false
                    },
                    annotationData: []
                }
            };
        }
        return {};
    };

    const [allChartsData, setAllChartsData] = useState(getInitialChartsData());
    const [allEntities, setAllEntities] = useState(() =>
        initialChartKeyRef.current ? { [initialChartKeyRef.current]: [] } : {}
    );
    const [allEvents, setAllEvents] = useState(() =>
        initialChartKeyRef.current ? { [initialChartKeyRef.current]: [] } : {}
    );
    const [totalEntitiesCount, setTotalEntitiesCount] = useState(routeParams?.events ? 1 : 0);
    const [showChartRemoveAlert, setShowChartRemoveAlert] = useState(false);
    const [allChartsIds, setAllChartsIds] = useState([]);
    const [chartsLoadingState, setChartsLoadingState] = useState(() =>
        initialChartKeyRef.current ? { [initialChartKeyRef.current]: true } : {}
    );
    const zoomResetViewRefs = useRef({});
    const [intervalSeconds, setIntervalSeconds] = useState(300);
    const [isZoomed, setIsZoomed] = useState(false);
    const [zoomedRange, setZoomedRange] = useState(null);
    const zoomTimeoutRef = useRef(null);

    /**
     * Setup digital twin hierarchy for extracting full entity information
     * Returns the level signals response for auto-selecting metrics
     */
    const setupDigitalTwinHierarchy = useCallback(async () => {
        const entityTypes = getHierarchyEntityTypes(pageContent);
        const digitalTwinHierarchyResponse = await fetchDigitalTwinHierarchy(batteryID, entityTypes, t);

        const affectedType = routeParams?.entityAffectedType || 'battery';

        // Fetch the alert's curated related signals so we can (a) load level
        // signals for every entity type they reference and (b) seed the default
        // chart entities. Falls back to [] on any error/no-data.
        const relatedSignals = await getAlertRelatedSignalsList(routeParams?.alertMetaID);
        relatedSignalsRef.current = relatedSignals;

        // Load continuous level signals for the affected level PLUS every entity
        // type present in the related-signals list, so the hierarchy tree has
        // leaf signals at each level we may need to map onto. The affected level
        // is kept first so getFirst4Metrics (fallback) still reads index 0.
        const levelSet = new Set([affectedType]);
        relatedSignals.forEach((s) => {
            if (s?.entityType) levelSet.add(s.entityType);
        });
        const uniqueLevels = Array.from(levelSet);
        const digitalTwinLevelSignals = await fetchDigitalTwinHierarchyLevelSignals(batteryID, uniqueLevels, 'CONTINUOUS');

        const transformedHierarchy = transformTree(digitalTwinHierarchyResponse ?? [], digitalTwinLevelSignals);
        setHierarchyInputData(transformedHierarchy);

        // Return the signals response for auto-selecting first 4 metrics
        return digitalTwinLevelSignals;
    }, [batteryID, pageContent, t, routeParams?.entityAffectedType, routeParams?.alertMetaID]);

    /**
     * Setup events hierarchy for extracting full event information
     */
    const setupEventsHierarchy = useCallback(async () => {
        const mergedHierarchy = await buildEventsHierarchy({ hasSmartAlertsAccess });
        setEventHierarchyInputData(mergedHierarchy);
    }, [hasSmartAlertsAccess]);

    /**
     * Extract first 4 metrics from level signals response
     */
    const getFirst4Metrics = useCallback((levelSignalsResponse) => {
        if (!levelSignalsResponse || !Array.isArray(levelSignalsResponse) || levelSignalsResponse.length === 0) {
            return [];
        }

        // levelSignalsResponse structure: [{ battery: [...signals] }]
        const batterySignals = levelSignalsResponse[0]?.[routeParams?.entityAffectedType || 'battery'] || [];

        // Get first 4 signals
        const first4Signals = batterySignals.slice(0, 4);

        // Extract just the metric keys
        return first4Signals.map(signal => {
            const formattedLabel = signal.label.toLowerCase().replaceAll(" ", "_");
            return formattedLabel;
        });
    }, []);

    /**
     * Find the path to a node in the transformed hierarchy by its systemID.
     * Returns the dot-separated path string (e.g. "batterysystem", "tms1",
     * "batterysystem.string1"), or null if not found.
     */
    const findNodePathById = useCallback((hierarchy, targetId) => {
        if (!targetId || !hierarchy) return null;
        const targetStr = String(targetId);

        const traverse = (node, currentPath) => {
            if (node.id && String(node.id) === targetStr) return currentPath;
            if (node.children) {
                for (const [key, child] of Object.entries(node.children)) {
                    if (child.type === 'complex') {
                        const result = traverse(child, `${currentPath}.${key}`);
                        if (result) return result;
                    }
                }
            }
            return null;
        };

        for (const [rootKey, rootNode] of Object.entries(hierarchy)) {
            const result = traverse(rootNode, rootKey);
            if (result) return result;
        }
        return null;
    }, []);

    /**
     * Transform metric names to entity format using hierarchy.
     * Finds the affected entity node by its systemID so that signals for any
     * entity level (battery, string, module, cell, tms, vehicle, pack, …) are
     * resolved correctly.
     */
    const transformRouteParamsToEntities = useCallback((metrics, batteryID, hierarchy) => {
        if (!metrics || !Array.isArray(metrics) || metrics.length === 0 || !hierarchy || Object.keys(hierarchy).length === 0) {
            return [];
        }

        const { entityAffectedId } = routeParams;

        // Find the entity node's position in the hierarchy by its database ID.
        // Falls back to the battery-system root when the node cannot be located.
        const entityPath = findNodePathById(hierarchy, entityAffectedId) || 'batterysystem';

        const paths = metrics.map(metricName => `${entityPath}.${metricName}`);

        const extractedEntities = extractEntities(batteryID, paths, hierarchy);
        const shuffledColors = getShuffledColors();

        return extractedEntities.map((entity, index) => ({
            ...entity,
            color: shuffledColors[index % shuffledColors.length],
            yaxis: "Y1"
        }));
    }, [findNodePathById]);

    /**
     * Transform route params (event names) to event format using event hierarchy
     */
    const transformRouteParamsToEvents = useCallback((eventNames, batteryID, eventHierarchy) => {
        if (!eventNames || !Array.isArray(eventNames) || eventNames.length === 0 || !eventHierarchy || Object.keys(eventHierarchy).length === 0) {
            return [];
        }

        // Route params come from the "Advanced Analytics" entry point on a
        // faults/alerts listing. eventsSource says which root the events belong
        // to so they resolve under the correct hierarchy branch.
        const rootName = routeParams?.eventsSource === ANNOTATION_SOURCE.SMART_ALERT ? "smartalerts" : "bmsfaults";

        const paths = eventNames.map(eventName => {
            const formatttedEventName = eventName.toLowerCase().replaceAll(" ", "_");
            return `${rootName}.${formatttedEventName}`;
        });

        const extractedEvents = extractEntities(batteryID, paths, eventHierarchy);

        return extractedEvents;
    }, [routeParams?.eventsSource]);

    // Fetch combined BMS-fault + smart-alert annotations for the selected events.
    const fetchAnnotationsForEvents = useCallback(
        (events, dateValue) => fetchEventAnnotations(events, dateValue, batteryID),
        [batteryID]
    );

    const handleAnyChartZoom = useCallback((xaxis) => {
        // Clear any existing timeout
        if (zoomTimeoutRef.current) {
            clearTimeout(zoomTimeoutRef.current);
        }

        // Show all zoom reset buttons
        Object.keys(zoomResetViewRefs.current).forEach((key) => {
          zoomResetViewRefs.current[key]?.current.show();
        });

        // Debounce the zoom update to prevent multiple rapid calls
        zoomTimeoutRef.current = setTimeout(async () => {
            // Use the xaxis parameter from beforeZoom event
            const minX = xaxis?.min;
            const maxX = xaxis?.max;

            if (!minX || !maxX) {
                return;
            }

            // Calculate the appropriate interval based on the visible time window
            const calculatedInterval = calculateIntervalFromTimeWindow(minX, maxX);

            // Convert minX and maxX timestamps to date strings for the API
            const zoomedStartDate = formatChartTimestampForApi(minX);
            const zoomedEndDate = formatChartTimestampForApi(maxX);

            // Store zoom state
            setIsZoomed(true);
            setZoomedRange({ minX, maxX });
            setIntervalSeconds(calculatedInterval);

            // Fetch new data for all charts with the zoomed range
            const allEntityKeys = Object.keys(allEntities);

            // Set loading state for all charts
            const loadingStates = {};
            allEntityKeys.forEach(key => {
                loadingStates[key] = true;
            });
            setChartsLoadingState(loadingStates);

            try {
                // Run all API calls in parallel
                const results = await Promise.all(
                    allEntityKeys.map(async (key) => {
                        const chartDataObj = allChartsData[key];
                        const { isValid } = chartDataObj?.chartData || {};

                        if (!isValid) {
                            return { key, skipUpdate: true };
                        }

                        const entities = allEntities[key];
                        const resetChartData = await resetContinuousChartData(
                            entities,
                            zoomedStartDate,
                            zoomedEndDate,
                            calculatedInterval
                        );

                        return { key, chartsData: resetChartData, skipUpdate: false };
                    })
                );

                let combinedAllChartsData = {}

                for (const key of allEntityKeys) {
                    const item = results.find((r) => r.key === key);
                    if (!item.skipUpdate) {
                        combinedAllChartsData[key] = {
                            chartData: item.chartsData,
                            annotationData: allChartsData[key]?.annotationData || []
                        };
                    } else {
                        combinedAllChartsData[key] = allChartsData[key];
                    }
                }

                setAllChartsData(combinedAllChartsData);
            } finally {
                // Clear loading state for all charts
                const loadingStates = {};
                allEntityKeys.forEach(key => {
                    loadingStates[key] = false;
                });
                setChartsLoadingState(loadingStates);
            }
        }, 500); // 500ms debounce
    }, [allEntities, allChartsData]);

    const handleResetAll = useCallback(async () => {
        // Reset zoom state
        const defaultInterval = 300;
        setIntervalSeconds(defaultInterval);
        setIsZoomed(false);
        setZoomedRange(null);

        // Clear any pending zoom timeouts
        if (zoomTimeoutRef.current) {
            clearTimeout(zoomTimeoutRef.current);
        }

        Object.keys(zoomResetViewRefs.current).forEach((key) => {
            zoomResetViewRefs.current[key]?.current.reset();
        });

        // Refetch all charts with original date range and default interval
        const allEntityKeys = Object.keys(allEntities);

        if (allEntityKeys.length > 0) {
            // Set loading state for all charts
            const loadingStates = {};
            allEntityKeys.forEach(key => {
                loadingStates[key] = true;
            });
            setChartsLoadingState(loadingStates);

            try {
                // Run all API calls in parallel
                const results = await Promise.all(
                    allEntityKeys.map(async (key) => {
                        const chartDataObj = allChartsData[key];
                        const { isValid } = chartDataObj?.chartData || {};

                        if (!isValid) {
                            return { key, skipUpdate: true };
                        }

                        const entities = allEntities[key];
                        const resetChartData = await resetContinuousChartData(
                            entities,
                            selectedDate?.value,
                            selectedDate?.value,
                            defaultInterval
                        );

                        return { key, chartsData: resetChartData, skipUpdate: false };
                    })
                );

                let combinedAllChartsData = {}

                for (const key of allEntityKeys) {
                    const item = results.find((r) => r.key === key);
                    if (!item.skipUpdate) {
                        combinedAllChartsData[key] = {
                            chartData: item.chartsData,
                            annotationData: allChartsData[key]?.annotationData || []
                        };
                    } else {
                        combinedAllChartsData[key] = allChartsData[key];
                    }
                }

                setAllChartsData(combinedAllChartsData);
            } finally {
                // Clear loading state for all charts
                const loadingStates = {};
                allEntityKeys.forEach(key => {
                    loadingStates[key] = false;
                });
                setChartsLoadingState(loadingStates);
            }
        }

        // this is required in order to ensure the chart groups are synced together after zoom reset
        setTimeout(() => {
            allChartsIds.forEach(id => {
              ApexCharts.exec(id, 'updateOptions', {
                chart: { group: chartGroupName },   // rebind group
                xaxis: getBaseXAxisSettings(selectedPrimaryGranularity, [], selectedDate) // <-- pass the exact xaxis object from parent
              }, false, true);
            });
          }, 0);
      }, [allChartsIds, chartGroupName, selectedPrimaryGranularity, selectedDate, allEntities, allChartsData]);

    const getEmptyData = () => {
        const randomChartKey = `chart-${Math.random()}`
        const emptyEntities = {
            [randomChartKey]: []
        }
        const emptyEvents = {
            [randomChartKey]: []
        }
        const emptyChartsData = {
            [randomChartKey]: {
                chartData: {
                    isValid: false
                },
                annotationData: []
            }
        }
        return { chartKey: randomChartKey, entities: emptyEntities, events: emptyEvents, chartsData: emptyChartsData }
    }

    const addEmptyChart = async (addAfterIndex = 0) => {
        const {chartKey, entities, events, chartsData} = getEmptyData();

        const newEntitiesEntry = [chartKey, entities[chartKey]];
        const newEventsEntry = [chartKey, events[chartKey]];
        const newChartsDataEntry = [chartKey, chartsData[chartKey]];

        const allEntitiesEntries = Object.entries(allEntities); // Convert to entries
        allEntitiesEntries.splice(addAfterIndex + 1, 0, newEntitiesEntry); // Insert after index
        const updatedAllEntities = Object.fromEntries(allEntitiesEntries);

        const allEventsEntries = Object.entries(allEvents); // Convert to entries
        allEventsEntries.splice(addAfterIndex + 1, 0, newEventsEntry); // Insert after index
        const updatedAllEvents = Object.fromEntries(allEventsEntries);

        const allChartsDataEntries = Object.entries(allChartsData); // Convert to entries
        allChartsDataEntries.splice(addAfterIndex + 1, 0, newChartsDataEntry); // Insert after index
        const updatedAllChartsData = Object.fromEntries(allChartsDataEntries);

        setAllEntities(updatedAllEntities);
        setAllEvents(updatedAllEvents);
        setAllChartsData(updatedAllChartsData);
    }

    const handleSaveChart = useCallback(async (key, entities, events = []) => {
        const chartKey = key;

        // Set loading state for this chart
        setChartsLoadingState(prev => ({
            ...prev,
            [chartKey]: true
        }));

        // Set a placeholder chart data with isValid=true so the chart renders with loader
        setAllChartsData((prev) => ({
            ...prev,
            [chartKey]: {
                chartData: {
                    isValid: true,
                    series: [],
                    metrics: [],
                    configs: {}
                },
                annotationData: []
            }
        }));

        try {
            const resetChartData = await resetContinuousChartData(entities, selectedDate?.value, selectedDate?.value, intervalSeconds);

            // Fetch annotation data (BMS faults + smart alerts) if events are selected
            const annotationData = await fetchAnnotationsForEvents(events, selectedDate?.value);

            // Store entities and events
            setAllEntities((prev) => ({
                ...prev,
                [chartKey]: entities,
            }));

            setAllEvents((prev) => ({
                ...prev,
                [chartKey]: events,
            }));

            setAllChartsData((prev) => ({
                ...prev,
                [chartKey]: {
                    chartData: resetChartData,
                    annotationData: annotationData
                }
            }));
        } finally {
            // Clear loading state for this chart
            setChartsLoadingState(prev => ({
                ...prev,
                [chartKey]: false
            }));
        }
    }, [selectedDate?.value, batteryID, fetchAnnotationsForEvents]);

    const handleRemoveChart = (key) => {
        setAllEntities((prev) => {
          const { [key]: _, ...rest } = prev;
          return rest;
        });

        setAllEvents((prev) => {
          const { [key]: _, ...rest } = prev;
          return rest;
        });

        setAllChartsData((prev) => {
          const { [key]: _, ...rest } = prev;
          return rest;
        });

        setChartsLoadingState((prev) => {
          const { [key]: _, ...rest } = prev;
          return rest;
        });

        // cleanup ref
        if (zoomResetViewRefs.current[key]) {
            delete zoomResetViewRefs.current[key];
        }
      }

    const refreshAllCharts = async () => {
        const allEntityKeys = Object.keys(allEntities);
        if (allEntityKeys.length <= 0) {
            const {entities, events, chartsData} = getEmptyData();
            setAllEntities(entities);
            setAllEvents(events);
            setAllChartsData(chartsData);
            return;
        }

        // Set loading state for all charts
        const loadingStates = {};
        allEntityKeys.forEach(key => {
            loadingStates[key] = true;
        });
        setChartsLoadingState(loadingStates);

        try {
            // Run all API calls in parallel
            const results = await Promise.all(
                allEntityKeys.map(async (key) => {
                    const chartDataObj = allChartsData[key];
                    const { isValid } = chartDataObj?.chartData || {};

                    if (!isValid) {
                        const { entities, events, chartsData } = getEmptyData();
                        return { key, entities: entities[Object.keys(entities)[0]], events: events[Object.keys(events)[0]], chartsData: chartsData[Object.keys(chartsData)[0]], annotationData: [] };
                    }

                    const entities = allEntities[key];
                    const events = allEvents[key];
                    const resetChartData = await resetContinuousChartData(
                        entities,
                        selectedDate?.value,
                        selectedDate?.value,
                        intervalSeconds
                    );

                    // Fetch annotation data (BMS faults + smart alerts) if events exist
                    const annotationData = await fetchAnnotationsForEvents(events, selectedDate?.value);

                    return { key, entities, events, chartsData: resetChartData, annotationData };
                })
            );

            let combinedEntities = {}
            let combinedEvents = {}
            let combinedAllChartsData = {}

            for (const key of allEntityKeys) {
                const item = results.find((r) => r.key === key);
                combinedEntities[key] = item.entities;
                combinedEvents[key] = item.events;
                combinedAllChartsData[key] = {
                    chartData: item.chartsData,
                    annotationData: item.annotationData
                };
            }

            setAllEntities(combinedEntities);
            setAllEvents(combinedEvents);
            setAllChartsData(combinedAllChartsData);
        } finally {
            // Clear loading state for all charts
            const loadingStates = {};
            allEntityKeys.forEach(key => {
                loadingStates[key] = false;
            });
            setChartsLoadingState(loadingStates);
        }
    }

    const getCsvName = (secondaryGranularity) => {
        let csvName = `Continous Signals - ${toTitleCase(selectedPrimaryGranularity)}`;
        if (selectedPrimaryGranularity === 'raw') {
            csvName = `${csvName}.csv`;
            return csvName;
        }
        csvName = `${csvName} - ${toTitleCase(secondaryGranularity)}.csv`;
        return csvName;
    }

    const downloadChartdata = (series, secondaryGranularity = 'avg') => {
        try {
            let csvData = getCSVDataForExportFromChart(entityIdDisplay, series, secondaryGranularity, t)
            DownloadCSV(getCsvName('continuous', secondaryGranularity), csvData);
        } catch (err) {
            console.log(err)
        }
    }

    // Annotations are a mix of BMS faults and smart alerts; each downloads from
    // its own endpoint (BMS ids to the diagnostics action, smart-alert ids to
    // the alerts action) so ids are never sent to the wrong backend.
    const downloadBmsFaultsData = async (annotationData) => {
        const bmsAnnotations = annotationData.filter(a => a.source !== ANNOTATION_SOURCE.SMART_ALERT);
        if (bmsAnnotations.length === 0) return;
        const res = await alertListAction({}, {
            resourceIDs: bmsAnnotations.map(annotation => annotation.incidentID),
            actionType: "download-csv",
            columnOrder: csvColumns.map(column => column.headerLabel),
            columns: JSON.stringify(csvColumns),
        });
        const csvData = res?.response?.data || [];
        DownloadCSV("Battery System Faults", csvData);
    }

    const downloadSmartAlertsData = async (annotationData) => {
        const smartAnnotations = annotationData.filter(a => a.source === ANNOTATION_SOURCE.SMART_ALERT);
        if (smartAnnotations.length === 0) return;
        const res = await platformAlertListAction({}, {
            resourceIDs: smartAnnotations.map(annotation => annotation.incidentID),
            actionType: "download-csv",
        });
        const csvData = res?.response?.data || [];
        buildPlatformAlertsCsv(csvData, "Smart Alerts");
    }

    // Initialize hierarchies when battery changes
    useEffect(() => {
        const initializeHierarchies = async () => {
            const {entities, events, chartsData} = getEmptyData();
            setAllEntities(entities);
            setAllEvents(events);
            setAllChartsData(chartsData);
            setShowChartRemoveAlert(false);

            // Setup hierarchies when battery changes
            if (batteryID) {
                const levelSignalsResponse = await setupDigitalTwinHierarchy();
                await setupEventsHierarchy();

                // Store level signals response in ref
                levelSignalsResponseRef.current = levelSignalsResponse;
            }
        };

        initializeHierarchies();
    }, [batteryID, setupDigitalTwinHierarchy, setupEventsHierarchy]);

    // Auto-load chart when hierarchies are ready and route params exist
    useEffect(() => {
        const attemptAutoLoad = async () => {
            // The root the incoming events belong to must be present in the
            // merged hierarchy before we process them — otherwise a smartAlert
            // route that fires before the smartalerts root is merged would
            // resolve zero events and never retry (hasProcessedRouteParams latches).
            const requiredRoot = routeParams?.eventsSource === ANNOTATION_SOURCE.SMART_ALERT ? "smartalerts" : "bmsfaults";
            const hasRequiredRoot = !!eventHierarchyInputData[requiredRoot];

            const shouldAutoLoad =
                routeParams?.events &&
                !hasProcessedRouteParams.current &&
                levelSignalsResponseRef.current &&
                selectedDate?.value &&
                Object.keys(hierarchyInputData).length > 0 &&
                hasRequiredRoot;

            if (shouldAutoLoad) {
                hasProcessedRouteParams.current = true;

                // Prefer the alert's curated related signals: map each onto the
                // nearest entity node(s) relative to the affected entity. Fall
                // back to the legacy "first 4 metrics of the affected level" when
                // no related signals are configured or none resolve to a node.
                let entities = [];
                const relatedSignals = relatedSignalsRef.current || [];
                if (relatedSignals.length > 0) {
                    const relatedPaths = buildRelatedSignalPaths({
                        relatedSignals,
                        entityAffectedType: routeParams?.entityAffectedType || 'battery',
                        entityAffectedId: routeParams?.entityAffectedId,
                        hierarchy: hierarchyInputData,
                    });
                    const extracted = extractEntities(batteryID, relatedPaths, hierarchyInputData);
                    const shuffledColors = getShuffledColors();
                    entities = extracted.map((entity, index) => ({
                        ...entity,
                        color: shuffledColors[index % shuffledColors.length],
                        yaxis: "Y1",
                    }));
                }

                if (entities.length === 0) {
                    // Fallback: first 4 metrics of the affected entity level.
                    const first4Metrics = getFirst4Metrics(levelSignalsResponseRef.current);
                    if (first4Metrics.length === 0) {
                        console.warn("No metrics found in level signals response");
                        return;
                    }
                    entities = transformRouteParamsToEntities(first4Metrics, batteryID, hierarchyInputData);
                }

                const events = transformRouteParamsToEvents(routeParams.events, batteryID, eventHierarchyInputData);

                if (entities.length === 0) {
                    console.warn("Failed to transform metrics to entities");
                    return;
                }

                // Use the pre-initialized chart key
                const chartKey = initialChartKeyRef.current;

                // Auto-save the chart with the entities and events
                // The chart data, entities, and events are already initialized in state
                await handleSaveChart(chartKey, entities, events);
            }
        };

        attemptAutoLoad();
    }, [routeParams?.events, routeParams?.eventsSource, selectedDate?.value, hierarchyInputData, eventHierarchyInputData, batteryID, getFirst4Metrics, transformRouteParamsToEntities, transformRouteParamsToEvents, handleSaveChart]);

    useEffect(() => {
        const allChartsLength = Object.keys(allChartsData).length;
        setTotalEntitiesCount(allChartsLength);
        const chartsIds = []
        Object.keys(allChartsData).forEach(key => {
            chartsIds.push(`continuous-${key}`);
        });
        setAllChartsIds(chartsIds);
    }, [allChartsData]);

    useEffect(() => {
        // Reset zoom state when the date changes so the new data renders
        // against the full date range rather than the previous zoomed window.
        if (isZoomed) {
            setIsZoomed(false);
            setZoomedRange(null);
            setIntervalSeconds(300);
            if (zoomTimeoutRef.current) clearTimeout(zoomTimeoutRef.current);
            Object.keys(zoomResetViewRefs.current).forEach((key) => {
                zoomResetViewRefs.current[key]?.current?.reset();
            });
        }
        refreshAllCharts();
    }, [selectedDate?.value]);

    return (
        <Grid2 container flexDirection='column' gap={1.5}>
            {Object.keys(allChartsData).map((key, index) => {
                const chartDataObj = allChartsData?.[key];
                const { isValid, series, metrics, configs } = chartDataObj?.chartData || {};
                const annotationData = chartDataObj?.annotationData || [];
                if (!isValid) {
                    return (
                        <Grid2 container flexDirection='column' gap={1.5}>
                            <CreateChartPlaceholder
                                batteryID={batteryID}
                                selectedDate={selectedDate}
                                onClose={() => handleRemoveChart(key)}
                                onCreateClick={handleSaveChart}
                                entityKey={key}
                                entityIndex={index}
                                totalEntitiesCount={totalEntitiesCount}
                                batteryInfo={batteryInfo}
                                selectedEntity={selectedEntity}
                            />
                            <AddChartButton onClick={() => addEmptyChart(index)} />
                        </Grid2>
                    );
                }
                let secondaryGranularityForChart = configs?.secondaryGranularity || 'avg';
                const isRawGranularity = selectedPrimaryGranularity === 'raw';
                if (!zoomResetViewRefs.current[key]) {
                    zoomResetViewRefs.current[key] = createRef();
                }
                return (
                    <Grid2 container flexDirection='column' gap={1.5}>
                        <Box key={key} sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Paper elevation={0} sx={{ width: 'fit-content', px: 1.5, py: 1, borderRadius: 0, borderTopLeftRadius: 8, borderTopRightRadius: 8, border: "1px solid #EAEAEA", borderBottom: 'none' }}>
                                <Grid2 container gap={1} alignItems={'center'}>
                                    <Typography variant="fw500s12lh20c000000">Visualization {index + 1}</Typography>
                                    {totalEntitiesCount > 1 && (
                                        <>
                                            <IconButton
                                                onClick={() => setShowChartRemoveAlert(true)}
                                                sx={{
                                                    p: 0.5,
                                                    '&:hover': {
                                                        backgroundColor: '#E6E6E6',
                                                        'svg': {
                                                            stroke: '#930500'
                                                        }
                                                    },
                                                    '&:focus': {
                                                        backgroundColor: '#E6E6E6',
                                                        'svg': {
                                                            stroke: '#930500'
                                                        }
                                                    }
                                                }}
                                            >
                                                <Close color={'#494949'} sx={{ height: '12px', width: '12px', stroke: '#686868' }} />
                                            </IconButton>
                                            <CloseChartAlertDialog
                                                open={showChartRemoveAlert}
                                                onClose={() => setShowChartRemoveAlert(false)}
                                                onConfirm={() => {
                                                    setShowChartRemoveAlert(false);
                                                    handleRemoveChart(key);
                                                }}
                                            />
                                        </>
                                    )}
                                </Grid2>
                            </Paper>
                            <Paper elevation={0} sx={{ borderRadius: 3, borderTopLeftRadius: 0, border: "1px solid #EAEAEA", height: '550px' }}>
                                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 3 }}>
                                    <Grid2 container justifyContent={'flex-end'} alignItems={'center'} gap={1}>
                                        {/* <Grid2 container sx={{ background: '#F9F9F9', padding: '4px', borderRadius: '6px' }}>
                                            {secondaryGranularities.map(granularity => {
                                                const isActive = !isRawGranularity && secondaryGranularityForChart === granularity
                                                return (
                                                    <PrimaryTooltip key={granularity} title={!isRawGranularity ? getSecondaryTooltipTitle(granularity) : undefined} placement="bottom" >
                                                        <SecondaryGranularityButton
                                                            disabled={isRawGranularity}
                                                            key={granularity}
                                                            onClick={() => {}}
                                                            active={isActive}
                                                        >
                                                            <CustomIcon height="12px" name={granularity === 'avg' ? "trend-line-icon" : "filled-trend-line-icon"} />
                                                        </SecondaryGranularityButton>
                                                    </PrimaryTooltip>
                                                )
                                            })}
                                        </Grid2> */}
                                        <EntityIdDisplayBox selectedEntity={selectedEntity} batteryInfo={batteryInfo} />
                                        <ZoomResetView
                                            ref={zoomResetViewRefs.current[key]}
                                            chartID={allChartsIds}
                                            containerStyles={{mr: 0}}
                                            iconButtonStyles={{py: 0.5, width: '28px', height: '28px'}}
                                            onReset={handleResetAll}
                                            isZoomedProp={isZoomed}
                                        />
                                        <EditVisualizationDialog chartId={`preview-continuous-${key}`} batteryID={batteryID} selectedDate={selectedDate} entities={allEntities[key]} events={allEvents[key]} onSaveChart={(entities, events) => handleSaveChart(key, entities, events)} batteryInfo={batteryInfo} selectedEntity={selectedEntity} />
                                        <Box>
                                            <ChartActions
                                                actions={[
                                                    { type: 'nothing', label: 'Download Continuous Data', icon: 'download-file-icon', onClick: () => downloadChartdata(series) },
                                                    {
                                                        type: 'nothing',
                                                        label: 'Download Battery System Faults Data',
                                                        icon: 'download-file-icon',
                                                        onClick: () => downloadBmsFaultsData(annotationData),
                                                        disabled: !annotationData.some(a => a.source !== ANNOTATION_SOURCE.SMART_ALERT),
                                                        tooltip: !annotationData.some(a => a.source !== ANNOTATION_SOURCE.SMART_ALERT) ? (
                                                            <Box sx={{ textAlign: 'center' }}>
                                                                <Typography variant="inherit" sx={{ fontSize: '12px', fontWeight: 400 }}>No faults available</Typography>
                                                            </Box>
                                                        ) : undefined
                                                    },
                                                    {
                                                        type: 'nothing',
                                                        label: 'Download Smart Alerts Data',
                                                        icon: 'download-file-icon',
                                                        onClick: () => downloadSmartAlertsData(annotationData),
                                                        disabled: !annotationData.some(a => a.source === ANNOTATION_SOURCE.SMART_ALERT),
                                                        tooltip: !annotationData.some(a => a.source === ANNOTATION_SOURCE.SMART_ALERT) ? (
                                                            <Box sx={{ textAlign: 'center' }}>
                                                                <Typography variant="inherit" sx={{ fontSize: '12px', fontWeight: 400 }}>No smart alerts available</Typography>
                                                            </Box>
                                                        ) : undefined
                                                    },
                                                ]}
                                            />
                                        </Box>
                                    </Grid2>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', width: '100%' }}>
                                        <ContinuousSignalChart
                                            chartID={`continuous-${key}`}
                                            metrics={metrics}
                                            key={`continuous-${key}`}
                                            isLoading={chartsLoadingState[key] || false}
                                            series={series}
                                            categories={[]}
                                            startDate={selectedDate}
                                            primaryGranularity={selectedPrimaryGranularity}
                                            visibleSeries={series.map(data => true)}
                                            zoomResetViewRef={zoomResetViewRefs.current[key]}
                                            onAnyZoom={handleAnyChartZoom}
                                            groupName={chartGroupName}
                                            zoomedRange={zoomedRange}
                                            setLoading={(loading) => {
                                                setChartsLoadingState(prev => ({
                                                    ...prev,
                                                    [key]: loading
                                                }));
                                            }}
                                            isZoomed={isZoomed}
                                            annotationData={annotationData}
                                            batteryInfo={batteryInfo}
                                            selectedEntity={selectedEntity}
                                        />
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>
                        <AddChartButton onClick={() => addEmptyChart(index)} />
                    </Grid2>
                )
            })}
        </Grid2>
    )
}

export default ContinuousChartVisualization;
